import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import MainLayout from '../../components/layout/MainLayout';
import {
  Lock, Users, Search, Save, CheckCircle2,
  Shield, LayoutDashboard, Briefcase, FileText, Building2,
  FileCheck, Receipt, Video, Calendar, BarChart3, Settings,
  Search as SearchIcon, Loader2
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';

const iconMap = {
  LayoutDashboard, Briefcase, Search: SearchIcon, FileText, Building2,
  FileCheck, Users, Receipt, Video, Calendar, BarChart3, Settings, Lock, Shield,
};

const PageAccessManager = () => {
  const [users, setUsers] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editablePermissions, setEditablePermissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [usersData, pagesData] = await Promise.all([
          adminApi.getAllUsers('limit=100'),
          adminApi.getPages(),
        ]);
        setUsers(usersData);
        setPages(pagesData);
      } catch (err) {
        toast.error(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const userPages = pages.filter(p => !p.is_admin_only);

  const selectUser = (user) => {
    setSelectedUser(user);
    setEditablePermissions(user.page_permissions || []);
    setHasChanges(false);
  };

  const togglePermission = (pageId) => {
    setEditablePermissions(prev =>
      prev.includes(pageId) ? prev.filter(p => p !== pageId) : [...prev, pageId]
    );
    setHasChanges(true);
  };

  const toggleAll = (grant) => {
    setEditablePermissions(grant ? userPages.map(p => p.id) : []);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await adminApi.updatePermissions(selectedUser.id, editablePermissions);
      toast.success(`Permissions updated for ${selectedUser.full_name}`);
      setHasChanges(false);
      // Update local state without full reload
      setUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, page_permissions: editablePermissions } : u));
      setSelectedUser(prev => ({ ...prev, page_permissions: editablePermissions }));
    } catch (err) {
      toast.error(err.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <MainLayout title="Page Access Manager" subtitle="Control user permissions for each page">
      <div data-testid="page-access-manager">
        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center gap-4"
        >
          <Lock className="w-6 h-6 text-blue-500" />
          <div>
            <p className="font-medium">Page Access Control</p>
            <p className="text-sm text-gray-500">Configure which pages each user can access. Admin pages are only visible to administrators.</p>
          </div>
        </motion.div>

        {/* Action Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-3 w-80 rounded-xl input-glass"
              data-testid="pam-search-input"
            />
          </div>
          {hasChanges && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-2 px-5 py-3 rounded-xl btn-primary disabled:opacity-60"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={saving}
              data-testid="pam-save-button"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Save Changes
            </motion.button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32" data-testid="pam-loading">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-6">
            {/* Users List */}
            <div className="glass-card p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Users ({filteredUsers.length})
              </h3>
              <div className="space-y-2" data-testid="pam-users-list">
                {filteredUsers.map((user) => (
                  <motion.button
                    key={user.id}
                    data-testid={`pam-user-${user.id}`}
                    onClick={() => selectUser(user)}
                    className={`w-full p-3 rounded-xl text-left transition-all flex items-center justify-between ${
                      selectedUser?.id === user.id
                        ? 'bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white'
                        : 'hover:bg-gray-500/10'
                    }`}
                    whileHover={{ x: selectedUser?.id === user.id ? 0 : 4 }}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold ${
                        selectedUser?.id === user.id ? 'bg-white/20' : 'bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF]'
                      }`}>
                        {user.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium">{user.full_name}</p>
                        <p className={`text-xs ${selectedUser?.id === user.id ? 'text-white/70' : 'text-gray-500'}`}>
                          {user.role === 'admin' ? 'All pages' : `${(user.page_permissions || []).length} pages`}
                        </p>
                      </div>
                    </div>
                    {user.role === 'admin' && (
                      <Shield className={`w-4 h-4 ${selectedUser?.id === user.id ? 'text-yellow-300' : 'text-purple-500'}`} />
                    )}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Permissions Panel */}
            <div className="col-span-2 glass-card p-6">
              {selectedUser ? (
                <div data-testid="pam-permissions-panel">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] flex items-center justify-center text-white text-xl font-bold">
                        {selectedUser.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">{selectedUser.full_name}</h3>
                        <p className="text-sm text-gray-500">{selectedUser.email}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${selectedUser.role === 'admin' ? 'bg-purple-500/20 text-purple-500' : 'bg-blue-500/20 text-blue-500'}`}>
                          {selectedUser.role}
                        </span>
                      </div>
                    </div>
                    {selectedUser.role !== 'admin' && (
                      <div className="flex items-center gap-2">
                        <motion.button onClick={() => toggleAll(true)} className="px-3 py-2 rounded-lg bg-green-500/20 text-green-500 text-sm" whileHover={{ scale: 1.02 }} data-testid="pam-grant-all">
                          Grant All
                        </motion.button>
                        <motion.button onClick={() => toggleAll(false)} className="px-3 py-2 rounded-lg bg-red-500/20 text-red-500 text-sm" whileHover={{ scale: 1.02 }} data-testid="pam-revoke-all">
                          Revoke All
                        </motion.button>
                      </div>
                    )}
                  </div>

                  {selectedUser.role === 'admin' ? (
                    <div className="space-y-2">
                      <p className="text-sm text-purple-500 flex items-center gap-2 mb-4">
                        <Shield className="w-4 h-4" /> Administrators have full access to all pages.
                      </p>
                      {pages.map((page) => {
                        const Icon = iconMap[page.icon] || FileText;
                        return (
                          <div key={page.id} className="flex items-center justify-between p-4 rounded-xl bg-purple-500/10">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-purple-500/20 text-purple-500"><Icon className="w-5 h-5" /></div>
                              <div>
                                <p className="font-medium">{page.name}</p>
                                <p className="text-xs text-gray-500">{page.path}</p>
                              </div>
                            </div>
                            <span className="text-sm text-purple-500 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />Admin Access</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-gray-500 mb-3">User Pages</h4>
                      {userPages.map((page) => {
                        const hasAccess = editablePermissions.includes(page.id);
                        const Icon = iconMap[page.icon] || FileText;
                        return (
                          <motion.div
                            key={page.id}
                            className={`flex items-center justify-between p-4 rounded-xl transition-all ${hasAccess ? 'bg-green-500/10' : 'bg-gray-500/5'}`}
                            whileHover={{ x: 4 }}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${hasAccess ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'}`}>
                                <Icon className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="font-medium">{page.name}</p>
                                <p className="text-xs text-gray-500">{page.path}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className={`text-sm ${hasAccess ? 'text-green-500' : 'text-gray-500'}`}>{hasAccess ? 'Allowed' : 'Denied'}</span>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={hasAccess}
                                  onChange={() => togglePermission(page.id)}
                                  className="sr-only peer"
                                  data-testid={`pam-toggle-${page.id}`}
                                />
                                <div className="w-11 h-6 bg-gray-500/30 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                              </label>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <Lock className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">Select a user to manage permissions</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default PageAccessManager;
